import { useEffect, useState, useRef, useCallback } from "react";
import { useLocation } from "react-router-dom";
import { AnimatePresence } from "framer-motion";
import { api, formatApiError, getUpgradeInfo } from "../lib/api";
import { SITE_NAME } from "../constants/site";
import { UpgradeNotice } from "../components/plan/PlanUI";
import { CHAT } from "../constants/testIds";
import { ConversationSidebar } from "../components/chat/ConversationSidebar";
import { ExamBanner } from "../components/chat/ExamBanner";
import { ExamModeModal } from "../components/chat/ExamModal";
import { MaterialModal } from "../components/chat/MaterialModal";
import { QuizModal } from "../components/chat/QuizModal";
import { FlashcardsModal } from "../components/chat/FlashcardsModal";
import { MindMapModal } from "../components/chat/MindMapModal";
import { Markdown } from "../components/chat/Markdown";
import { ProcessingCard, MaterialChip, uploadMaterialFile } from "../components/chat/MaterialUpload";
import { FeedbackModal } from "../components/chat/FeedbackModal";
import {
  Send, Sparkles, FileText, GraduationCap, Brain, Focus, Menu, Loader2,
  Repeat2, Zap, BookOpen, Layers, Paperclip, UploadCloud, Network, Lock, Star, X
} from "lucide-react";

// Avaliação por conversa: "dismissed" (dispensou o convite) ou "rated" (já
// avaliou) — nos dois casos não perguntamos de novo naquela conversa.
const fbKey = (cid) => `ae_feedback_${cid}`;

export default function ChatPage() {
  const [conversations, setConversations] = useState([]);
  const [activeId, setActiveId] = useState(null);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [sending, setSending] = useState(false);
  const [exam, setExam] = useState(null);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [focusMode, setFocusMode] = useState(false);
  const [examModal, setExamModal] = useState(false);
  const [materialModal, setMaterialModal] = useState(false);
  const [quizModal, setQuizModal] = useState(false);
  const [flashcardsModal, setFlashcardsModal] = useState(false);
  const [mapaModal, setMapaModal] = useState(false);
  const [feedbackModal, setFeedbackModal] = useState(false);
  const [fbState, setFbState] = useState(null); // null | "dismissed" | "rated"
  const [recursos, setRecursos] = useState(null); // cadeados por plano (fonte: backend /me/uso)
  const [err, setErr] = useState(null);
  const [upgrade, setUpgrade] = useState(null);
  const [materials, setMaterials] = useState([]);
  const [processing, setProcessing] = useState(null); // {filename, stage: "reading"|"done"}
  const [dragOver, setDragOver] = useState(false);
  const bottomRef = useRef();
  const dragDepth = useRef(0);
  const firstLoad = useRef(true);

  const refreshConversations = useCallback(async () => {
    const { data } = await api.get("/conversations");
    setConversations(data);
    // Auto-seleciona só na primeira carga — depois de excluir a conversa aberta,
    // a tela fica limpa (nada de "ressuscitar" conversa).
    if (firstLoad.current && data.length > 0) {
      firstLoad.current = false;
      setActiveId((cur) => cur ?? data[0].id);
    }
    firstLoad.current = false;
    return data;
  }, []);

  const refreshExam = useCallback(async () => {
    const { data } = await api.get("/exam-mode");
    setExam(data);
  }, []);

  const refreshMaterials = useCallback(async (cid) => {
    if (!cid) { setMaterials([]); return; }
    try {
      const { data } = await api.get(`/materials?conversation_id=${cid}`);
      setMaterials(data);
    } catch { setMaterials([]); }
  }, []);

  useEffect(() => {
    refreshConversations();
    refreshExam();
    api.get("/me/uso").then((r) => setRecursos(r.data.recursos || {})).catch(() => {});
  }, [refreshConversations, refreshExam]);

  // Veio do Hub pelo card "Enviar material" → abre direto o upload
  const location = useLocation();
  useEffect(() => {
    if (location.state?.openMaterial) {
      setMaterialModal(true);
      window.history.replaceState({}, "");
    }
    // Veio do onboarding: primeira pergunta personalizada já engatilhada no input
    if (location.state?.sugestao) {
      setInput(location.state.sugestao);
      window.history.replaceState({}, "");
    }
  }, [location.state]);

  useEffect(() => {
    if (!activeId) { setMessages([]); setMaterials([]); setFbState(null); return; }
    api.get(`/conversations/${activeId}/messages`).then((r) => setMessages(r.data)).catch(() => {});
    refreshMaterials(activeId);
    try { setFbState(localStorage.getItem(fbKey(activeId))); } catch { setFbState(null); }
  }, [activeId, refreshMaterials]);

  const setFeedbackDone = (state) => {
    if (activeId) { try { localStorage.setItem(fbKey(activeId), state); } catch {} }
    setFbState(state);
  };

  // Convite leve, UMA vez por conversa, só depois de uma conversa de verdade (6+ msgs)
  const showFeedbackInvite = !!activeId && messages.length >= 6 && !fbState && !sending;

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages, sending]);

  const daysLeft = exam ? Math.max(0, Math.ceil((new Date(exam.date) - new Date()) / 86400000)) : null;

  const ensureConversation = async () => {
    if (activeId) return activeId;
    const { data } = await api.post("/conversations", { title: "Nova conversa" });
    await refreshConversations();
    setActiveId(data.id);
    return data.id;
  };

  // Exclusão da conversa aberta limpa TUDO (mensagens, materiais, id ativo)
  const onConversationDeleted = (id) => {
    if (id === activeId) {
      setActiveId(null);
      setMessages([]);
      setMaterials([]);
      setUpgrade(null);
      setErr(null);
    }
  };

  // ---- Upload de material (drag & drop, clique, Ctrl+V) ----
  const handleFiles = async (fileList) => {
    const file = fileList?.[0];
    if (!file || processing?.stage === "reading") return;
    setErr(null); setUpgrade(null);
    setProcessing({ filename: file.name || "print colado", stage: "reading" });
    try {
      const cid = await ensureConversation();
      await uploadMaterialFile(file, cid);
      await refreshMaterials(cid);
      setProcessing((p) => ({ ...p, stage: "done" }));
      setTimeout(() => setProcessing((p) => (p?.stage === "done" ? null : p)), 3200);
    } catch (e) {
      setProcessing(null);
      const up = getUpgradeInfo(e);
      if (up) setUpgrade(up);
      else setErr(e?.response ? formatApiError(e) : (e?.message || "Não consegui ler esse arquivo."));
    }
  };

  const removeMaterial = async (mid) => {
    await api.delete(`/materials/${mid}`);
    refreshMaterials(activeId);
  };

  const onPaste = (e) => {
    const items = Array.from(e.clipboardData?.items || []);
    const img = items.find((i) => i.type?.startsWith("image/"));
    if (img) {
      e.preventDefault();
      const file = img.getAsFile();
      if (file) handleFiles([new File([file], `print-${Date.now()}.png`, { type: file.type })]);
    }
  };

  const send = async (textOverride) => {
    const text = (textOverride ?? input).trim();
    if (!text || sending) return;
    setSending(true); setErr(null); setUpgrade(null);
    if (!textOverride) setInput("");
    const tempUser = { id: `tmp-${Date.now()}`, role: "user", content: text, created_at: new Date().toISOString() };
    try {
      const cid = await ensureConversation();
      // Optimistic add user msg
      setMessages((m) => [...m, tempUser]);
      const { data } = await api.post("/messages", { conversation_id: cid, content: text });
      setMessages((m) => {
        const filtered = m.filter((x) => x.id !== tempUser.id);
        return [...filtered, data.user_message, data.assistant_message];
      });
      refreshConversations();
    } catch (e) {
      const up = getUpgradeInfo(e);
      if (up) {
        // Limite do plano: a mensagem não foi enviada — devolve pro input e mostra o CTA
        setUpgrade(up);
        setMessages((m) => m.filter((x) => x.id !== tempUser.id));
        if (!textOverride) setInput(text);
      } else {
        setErr(formatApiError(e));
      }
    }
    setSending(false);
  };

  const onKey = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      send();
    }
  };

  const lastAssistant = [...messages].reverse().find((m) => m.role === "assistant");

  const quickActions = [
    { label: "Explica mais simples", icon: Zap, prompt: "Explica isso de um jeito mais simples, como se eu fosse leigo." },
    { label: "Dá um exemplo", icon: BookOpen, prompt: "Me dá um exemplo concreto disso." },
    { label: "Me testa sobre isso", icon: Repeat2, prompt: "Me faz 3 perguntinhas rápidas pra testar se entendi." },
    { label: "Virar flashcards", icon: Layers, action: () => setFlashcardsModal(true) },
  ];

  return (
    <div data-testid={CHAT.page} className={`flex h-[calc(100vh-64px)] md:h-[calc(100vh-64px)] ${focusMode ? "bg-background" : ""}`}>
      {/* Sidebar (desktop) */}
      <aside className="hidden md:block w-72 shrink-0">
        <ConversationSidebar
          activeId={activeId}
          onSelect={setActiveId}
          conversations={conversations}
          refresh={refreshConversations}
          onDeleted={onConversationDeleted}
        />
      </aside>

      {/* Mobile drawer */}
      {sidebarOpen && (
        <div className="md:hidden fixed inset-0 z-40 bg-black/60" onClick={() => setSidebarOpen(false)}>
          <aside className="absolute left-0 top-0 bottom-0 w-72 max-w-[85vw]" onClick={(e) => e.stopPropagation()}>
            <ConversationSidebar
              activeId={activeId}
              onSelect={setActiveId}
              conversations={conversations}
              refresh={refreshConversations}
              onClose={() => setSidebarOpen(false)}
              onDeleted={onConversationDeleted}
            />
          </aside>
        </div>
      )}

      {/* Main */}
      <section
        className="relative flex-1 flex flex-col min-w-0"
        onDragEnter={(e) => {
          if (e.dataTransfer?.types?.includes("Files")) {
            dragDepth.current += 1;
            setDragOver(true);
          }
        }}
        onDragLeave={() => {
          dragDepth.current = Math.max(0, dragDepth.current - 1);
          if (dragDepth.current === 0) setDragOver(false);
        }}
        onDragOver={(e) => e.preventDefault()}
        onDrop={(e) => {
          e.preventDefault();
          dragDepth.current = 0;
          setDragOver(false);
          if (e.dataTransfer.files?.length) handleFiles(e.dataTransfer.files);
        }}
      >
        {/* Overlay de drop */}
        {dragOver && (
          <div className="absolute inset-0 z-30 flex items-center justify-center rounded-xl border-2 border-dashed border-violet-400 bg-background/90 pointer-events-none">
            <div className="text-center space-y-2">
              <UploadCloud className="h-10 w-10 text-violet-400 mx-auto" strokeWidth={1.5} />
              <p className="font-semibold">Solte aqui o PDF ou a foto da matéria</p>
              <p className="text-xs text-muted-foreground">Vira material desta conversa — usado nas respostas, quiz e flashcards</p>
            </div>
          </div>
        )}

        {/* Top action bar */}
        {!focusMode && (
          <div className="border-b border-border bg-background px-3 py-2.5 flex items-center gap-2 overflow-x-auto scrollbar-thin">
            <button
              onClick={() => setSidebarOpen(true)}
              className="md:hidden rounded-full p-2 hover:bg-muted shrink-0"
            ><Menu className="h-4 w-4" /></button>

            <ToolbarButton testId={CHAT.examButton} active={!!exam} icon={Sparkles} label="Modo Prova" locked={recursos && !recursos.modo_prova} onClick={() => setExamModal(true)} />
            <ToolbarButton testId={CHAT.materialButton} active={materials.length > 0} icon={FileText} label={materials.length > 0 ? `Material (${materials.length})` : "Material"} locked={recursos && !recursos.upload_material} onClick={() => setMaterialModal(true)} disabled={!activeId} />
            <ToolbarButton testId={CHAT.quizButton} icon={GraduationCap} label="Quiz" onClick={() => setQuizModal(true)} disabled={!activeId} />
            <ToolbarButton testId={CHAT.flashcardsButton} icon={Brain} label="Flashcards" onClick={() => setFlashcardsModal(true)} disabled={!activeId} />
            <ToolbarButton testId="chat-mindmap-btn" icon={Network} label="Mapa mental" locked={recursos && !recursos.mapa_mental} onClick={() => setMapaModal(true)} disabled={!activeId} />
            <div className="flex-1" />
            <ToolbarButton testId="chat-feedback-btn" icon={Star} label="Avaliar" onClick={() => setFeedbackModal(true)} disabled={!activeId} />
            <ToolbarButton testId={CHAT.focusButton} icon={Focus} label="Foco" onClick={() => setFocusMode(true)} />
          </div>
        )}

        {focusMode && (
          <button
            onClick={() => setFocusMode(false)}
            className="absolute top-4 right-4 z-30 rounded-full bg-popover border border-border px-3 py-1.5 text-xs text-muted-foreground hover:text-foreground"
          >Sair do foco</button>
        )}

        {/* Exam banner */}
        {exam && !focusMode && (
          <div className="px-3 pt-3">
            <ExamBanner exam={exam} daysLeft={daysLeft} onOpen={() => setExamModal(true)} onClear={async () => { await api.delete("/exam-mode"); refreshExam(); }} />
          </div>
        )}

        {/* Messages */}
        <div className="flex-1 overflow-y-auto scrollbar-thin px-3 sm:px-6 py-4 space-y-4">
          {messages.length === 0 && !sending && !processing && (
            <EmptyState onPick={(p) => send(p)} hasExam={!!exam} onUploadClick={() => setMaterialModal(true)} />
          )}
          {messages.map((m) => (
            <MessageBubble key={m.id} m={m} />
          ))}
          {sending && (
            <div className="flex items-center gap-2 text-sm text-muted-foreground">
              <Loader2 className="h-3.5 w-3.5 animate-spin text-violet-400" /> Pensando...
            </div>
          )}
          <div ref={bottomRef} />
        </div>

        {/* Quick actions */}
        {lastAssistant && !sending && !focusMode && (
          <div className="px-3 sm:px-6 pb-2 flex gap-1.5 overflow-x-auto scrollbar-thin">
            {quickActions.map((qa) => (
              <button
                key={qa.label}
                onClick={() => qa.action ? qa.action() : send(qa.prompt)}
                className="shrink-0 inline-flex items-center gap-1.5 rounded-full border border-border bg-card hover:bg-violet-500/10 hover:border-violet-500/40 px-3 py-1.5 text-xs text-muted-foreground hover:text-violet-300 transition"
              >
                <qa.icon className="h-3 w-3" /> {qa.label}
              </button>
            ))}
          </div>
        )}

        {/* Convite discreto pra avaliar (some ao dispensar ou avaliar) */}
        {showFeedbackInvite && !focusMode && (
          <div data-testid="feedback-invite" className="mx-3 sm:mx-6 mb-2 flex items-center gap-2 rounded-xl border border-border bg-card px-3.5 py-2.5 text-xs text-muted-foreground">
            <Star className="h-3.5 w-3.5 text-amber-400 shrink-0" />
            <span className="flex-1">Curtiu essa conversa? Sua avaliação ajuda o tutor a melhorar.</span>
            <button
              data-testid="feedback-invite-open"
              onClick={() => setFeedbackModal(true)}
              className="shrink-0 rounded-full bg-violet-600 hover:bg-violet-500 text-white px-3 py-1.5 font-medium transition"
            >
              Avaliar
            </button>
            <button
              data-testid="feedback-invite-dismiss"
              onClick={() => setFeedbackDone("dismissed")}
              title="Não perguntar de novo nesta conversa"
              className="shrink-0 rounded-full p-1.5 hover:bg-muted"
            >
              <X className="h-3.5 w-3.5" />
            </button>
          </div>
        )}

        {/* Composer */}
        <div className="border-t border-border bg-background p-3 space-y-2">
          <AnimatePresence>
            {processing && (
              <ProcessingCard
                filename={processing.filename}
                stage={processing.stage}
                onDismiss={() => setProcessing(null)}
              />
            )}
          </AnimatePresence>
          {materials.length > 0 && !processing && (
            <div className="flex flex-wrap gap-1.5">
              <AnimatePresence>
                {materials.map((m) => (
                  <MaterialChip key={m.id} material={m} onRemove={removeMaterial} />
                ))}
              </AnimatePresence>
            </div>
          )}
          {upgrade && <UpgradeNotice info={upgrade} onDismiss={() => setUpgrade(null)} />}
          {err && <p className="text-xs text-red-400 px-1">{err}</p>}
          <div className="relative">
            <button
              data-testid="chat-attach-btn"
              onClick={() => setMaterialModal(true)}
              title="Enviar PDF, foto ou print (ou arrasta pro chat / Ctrl+V)"
              className="absolute left-2 bottom-2 rounded-full p-2 text-muted-foreground hover:text-violet-300 hover:bg-violet-500/10 transition"
            >
              <Paperclip className="h-4 w-4" />
            </button>
            <textarea
              data-testid={CHAT.messageInput}
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={onKey}
              onPaste={onPaste}
              placeholder="Pergunta sua dúvida... (Enter envia · arrasta PDF/foto ou cola print)"
              rows={1}
              className="w-full resize-none rounded-2xl bg-secondary pl-11 pr-12 py-3 text-sm outline-none focus:ring-2 focus:ring-violet-500/50 max-h-32 scrollbar-thin"
            />
            <button
              data-testid={CHAT.sendButton}
              onClick={() => send()}
              disabled={sending || !input.trim()}
              className="absolute right-2 bottom-2 rounded-full bg-violet-600 hover:bg-violet-500 text-white p-2 disabled:opacity-50 transition"
            >
              {sending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
            </button>
          </div>
          <p data-testid="ai-disclaimer" className="text-center text-[10px] leading-tight text-muted-foreground/70 px-2">
            ⚠️ {SITE_NAME} é uma inteligência artificial e pode cometer erros. Confira informações importantes.
          </p>
        </div>
      </section>

      {/* Modals */}
      <ExamModeModal open={examModal} onClose={() => setExamModal(false)} current={exam} onSaved={refreshExam} />
      <MaterialModal
        open={materialModal}
        onClose={() => { setMaterialModal(false); refreshMaterials(activeId); }}
        conversationId={activeId}
        ensureConversation={ensureConversation}
      />
      <QuizModal open={quizModal} onClose={() => setQuizModal(false)} conversationId={activeId} recursos={recursos} />
      <FlashcardsModal open={flashcardsModal} onClose={() => setFlashcardsModal(false)} conversationId={activeId} />
      <MindMapModal open={mapaModal} onClose={() => setMapaModal(false)} conversationId={activeId} locked={!!recursos && !recursos.mapa_mental} />
      <FeedbackModal
        open={feedbackModal}
        onClose={() => setFeedbackModal(false)}
        conversationId={activeId}
        onRated={() => setFeedbackDone("rated")}
      />
    </div>
  );
}

function ToolbarButton({ testId, icon: Icon, label, onClick, disabled, active, locked }) {
  return (
    <button
      data-testid={testId}
      onClick={onClick}
      disabled={disabled}
      title={locked ? "Recurso de plano superior — clique pra ver" : undefined}
      className={`shrink-0 inline-flex items-center gap-1.5 rounded-full px-3 py-1.5 text-xs font-medium transition border ${
        active
          ? "border-violet-500/60 bg-violet-500/15 text-violet-300"
          : "border-border bg-card text-muted-foreground hover:text-foreground hover:bg-muted"
      } disabled:opacity-40 disabled:pointer-events-none`}
    >
      <Icon className="h-3.5 w-3.5" /> {label}
      {locked && <Lock className="h-3 w-3 text-violet-400" />}
    </button>
  );
}

function MessageBubble({ m }) {
  const isUser = m.role === "user";
  return (
    <div data-testid={CHAT.messageBubble(m.id)} className={`flex ${isUser ? "justify-end" : "justify-start"}`}>
      <div className={`max-w-[88%] sm:max-w-[78%] rounded-2xl px-4 py-2.5 text-sm ${
        isUser
          ? "bg-violet-600 text-white rounded-br-md"
          : "bg-card border border-border rounded-bl-md"
      }`}>
        <Markdown>{m.content}</Markdown>
        {m.fair_use && (
          <p className="mt-2 text-[10px] text-amber-400/70 italic">↳ você usou MUITO hoje 💪 — respondi com o modelo padrão; amanhã o turbo volta</p>
        )}
        {m.fell_back && !m.fair_use && (
          <p className="mt-2 text-[10px] text-amber-400/70 italic">↳ usei modelo leve (modelo principal estava no limite)</p>
        )}
      </div>
    </div>
  );
}

function EmptyState({ onPick, hasExam, onUploadClick }) {
  const suggestions = hasExam ? [
    "O que cai na prova mesmo?",
    "Me dá 3 questões pra praticar",
    "Explica o tópico mais difícil",
  ] : [
    "Me ajuda a entender a Revolução Industrial",
    "Como resolvo equação do 2º grau?",
    "Explica fotossíntese em 2 minutos",
  ];
  return (
    <div className="flex flex-col items-center justify-center min-h-[300px] text-center space-y-5">
      <div className="rounded-full bg-violet-500/10 p-4">
        <Sparkles className="h-8 w-8 text-violet-400" strokeWidth={1.5} />
      </div>
      <div>
        <h2 className="text-xl font-bold tracking-tight">Pergunta o que quiser</h2>
        <p className="text-sm text-muted-foreground mt-1.5 max-w-md">
          Sou seu professor virtual. Te explico, te testo, monto seu material e travo no que vai cair na prova.
        </p>
      </div>
      <div className="flex flex-col gap-2 w-full max-w-md">
        {suggestions.map((s) => (
          <button
            key={s}
            onClick={() => onPick(s)}
            className="text-left rounded-xl border border-border bg-card hover:bg-violet-500/5 hover:border-violet-500/40 px-4 py-2.5 text-sm text-muted-foreground hover:text-foreground transition"
          >{s}</button>
        ))}
        <button
          onClick={onUploadClick}
          data-testid="empty-upload-cta"
          className="flex items-center justify-center gap-2 rounded-xl border-2 border-dashed border-violet-500/40 bg-violet-500/5 hover:bg-violet-500/10 px-4 py-3 text-sm text-violet-300 transition"
        >
          <UploadCloud className="h-4 w-4" /> Enviar PDF ou foto da matéria — vira a fonte das respostas
        </button>
      </div>
    </div>
  );
}
